<template>
  <layout-with-back-btn title="Detail Tagihan">
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[16px] border-[1px] border-[#E2E2E2] rounded-[8px] p-[16px] my-[34px]">
      <CardTagihan
        title="Nominal Tagihan"
        :text-content="IDR(summary.nominal_invoice_this_month)"
        :text-footer="`Jumlah Invoice Tertagih: ${summary.number_of_bill_this_month}`"
        :is-loading="isSummaryFetching"
      />
      <CardTagihan
        title="Nominal Pelunasan"
        :text-content="IDR(summary.nominal_invoice_paid_this_month)"
        :text-footer="`Jumlah Invoice Lunas: ${summary.number_of_bill_paid_this_month}`"
        :is-loading="isSummaryFetching"
      />
      <div class="order-first lg:!order-none sm:col-span-2 lg:col-span-1 flex items-start justify-end gap-[8px]">
        <b-button
          variant="primary"
          size="sm"
          class="!p-[6px]"
          @click="handleRefreshData"
        >
          <span class="k-rotate-left-1 text-[24px]" />
        </b-button>
        <date-picker
          v-model="period"
          :date-format="createDatePickerRestrictor()"
          :ranges="datePickerRangeGeneral"
          input-label-class="!text-[#222222]"
        />
      </div>
    </div>

    <div>
      <div class="flex items-center justify-end sm:justify-between gap-[10px] flex-wrap my-[24px]">
        <div class="d-flex rounded-lg align-items-center text-[#c2c2c2] border-[1px] border-[#e2e2e2] w-full sm:w-auto">
          <span
            class="k-search-normal-1 font-bold text-[24px] align-middle mx-[10px]"
          />
          <b-form-input
            v-model="search"
            placeholder="Cari nama partner, nama bisnis"
            class="sm:!w-[250px] !p-[10px] !pl-0 border-0 focus:!shadow-none !rounded-r-lg !text-[16px] !text-[#333333] !font-normal"
            debounce="500"
          />
        </div>
        <b-form-select
          v-model="status"
          :options="optionStatus"
          value-field="value"
          text-field="text"
          placeholder="Status"
          class="csw w-auto min-w-[150px] self-end m-0 !border-[1px] !border-[#e2e2e2] !rounded-lg !text-[14px] !text-[#333333] !h-[40px]"
        />
      </div>

      <b-table
        :items="fetcherAllTagihan.data"
        :fields="fieldsTagihanTable"
        :busy="fetcherAllTagihan.isLoading"
        show-empty
        empty-text="Tidak ada data yang ditampilkan."
        responsive
        :sticky-header="true"
        @scroll.native="handleScroll"
      >
        <template #cell(no)="data">
          {{ data.index + 1 }}
        </template>
        <template #cell(status)="data">
          <span
            :class="statusPillConfig[data.item.status || 'PENDING'].class"
            class="text-[12px] font-normal rounded-[4px] px-[8px] py-[2px] rounded-full"
          >
            {{ statusPillConfig[data.item.status || 'PENDING'].text }}
          </span>
        </template>
        <template #cell(no_hp)="data">
          <div class="flex items-center justify-start gap-[10px]">
            <div
              v-if="data.value"
              class="flex items-center gap-[4px]"
            >
              <span class="k-Whatsapp text-[24px] text-[#10673E]" />
              <a
                :href="`https://wa.me/${data.value}`"
                target="_blank"
                class="text-[#10673E] underline hover:text-[#333333] hover:undeline transition"
              >{{ data.value }}</a>
            </div>
            <div>
              <DownloadInvoice
                :no-invoice="data.item.ref_id"
                :partner-name="data.item.partner_name"
                :period="data.item.period"
                :status="data.item.status"
                :add-to-queue="queue.addToQueue"
              />
            </div>
          </div>
        </template>
      </b-table>
    </div>
  </layout-with-back-btn>
</template>

<script>
/**
 * Komponen untuk menampilkan tagihan kompack
 * @description - Komponen untuk menampilkan semua list tagihan dari user kompack.
 * Komponen ini memiliki beberapa fitur:
 * - Menampilkan ringkasan tagihan bulanan
 * - Menampilkan semua list tagihan dari user kompack
 * - Menampilkan detail tagihan
 * - Menampilkan form filter status tagihan
 * - Menampilkan form filter tanggal tagihan
 * - Dapat menampilkan list tagihan berdasarkan status, tanggal, dan pencarian nama partner atau nama bisnis
 * - Dapat mengunduh tagihan dalam format PDF dimana pdf tersebut akan di generate secara dinamis berdasarkan data yang ada dari sisi FE.
 * @returns {Object} - Object of component
 * @example
 * <Tagihan />
 */
import { today, last30 } from '@/store/helpers'
import { DAY_MONTH_YEAR, YEAR_MONTH_DAY, createDatePickerRestrictor } from '@/libs/filterDate'
import { IDR } from '@/libs/currency'
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import useQueueProcess from '@/@core/libs/queue/useQueueProcess'
import LayoutWithBackBtn from '../../LayoutWithBackBtn.vue'
import CardTagihan from './components/CardTagihan.vue'
import DatePicker from '../../DatePicker.vue'
import DownloadInvoice from './components/DownloadInvoice.vue'
import { datePickerRangeGeneral } from '../../config'
import {
  fieldsTagihanTable,
  optionStatusTagihan,
  statusPillConfig,
} from './config'

export default {
  components: {
    LayoutWithBackBtn,
    CardTagihan,
    DatePicker,
    DownloadInvoice,
  },
  data() {
    return {
      period: {
        startDate: this.$route.query.start_date || last30,
        endDate: this.$route.query.end_date || today,
      },
      search: '',
      status: '',
      optionStatus: optionStatusTagihan,
      summary: {},
      isSummaryFetching: true,
      fetcherAllTagihan: createPaginatedFetcher(this.fetchAllTagihan),
      queue: useQueueProcess(),
      fieldsTagihanTable,
      statusPillConfig,
    }
  },
  watch: {
    search(value) {
      this.fetcherAllTagihan.fetchData({
        options: {
          start_date: YEAR_MONTH_DAY(this.period.startDate),
          end_date: YEAR_MONTH_DAY(this.period.endDate),
          search: value,
          status: this.status,
        },
        reset: true,
      })
    },
    status(value) {
      this.fetcherAllTagihan.fetchData({
        options: {
          start_date: YEAR_MONTH_DAY(this.period.startDate),
          end_date: YEAR_MONTH_DAY(this.period.endDate),
          status: value,
          search: this.search,
        },
        reset: true,
      })
    },
    period(value) {
      this.fetchSummary()
      this.fetcherAllTagihan.fetchData({
        options: {
          start_date: YEAR_MONTH_DAY(value.startDate),
          end_date: YEAR_MONTH_DAY(value.endDate),
          search: this.search,
          status: this.status,
        },
        reset: true,
      })
    },
  },
  created() {
    this.datePickerRangeGeneral = datePickerRangeGeneral(today)
  },
  mounted() {
    this.handleRefreshData()
  },
  methods: {
    /**
     * @description Menghapus data cache dan mengambil ulang data tagihan dari server.
     */
    handleRefreshData() {
      this.fetcherAllTagihan.fetchData({
        options: {
          start_date: YEAR_MONTH_DAY(this.period.startDate),
          end_date: YEAR_MONTH_DAY(this.period.endDate),
          search: this.search,
          status: this.status,
        },
        reset: true,
      })
      this.fetchSummary()
    },
    DAY_MONTH_YEAR,
    IDR,
    createDatePickerRestrictor,
    /**
     * @description Menghapus data cache dan mengambil ulang data tagihan dari server.
     */
    handleScroll(event) {
      if (event.target.scrollTop + event.target.clientHeight >= event.target.scrollHeight) {
        this.fetcherAllTagihan.fetchData()
      }
    },
    /**
     * @description Mengambil data tagihan dari server.
     * @param {Object} params - Parameter untuk mengambil data tagihan
     * @param {number} params.limit - Jumlah data yang akan diambil
     * @param {string} params.search - Pencarian nama partner atau nama bisnis
     * @param {string} params.status - Status tagihan
     * @param {number} params.offset - Offset untuk paginasi
     * @param {string} params.start_date - Tanggal awal
     * @param {string} params.end_date - Tanggal akhir
     * @returns {Promise} - Promise dari pengambilan data tagihan
     */
    fetchAllTagihan({
      limit, search, status, offset, start_date, end_date,
    }) {
      return this.$http_new.get('/auth/api/v1/admin/user-balance-invoices/kompack', {
        params: {
          limit,
          offset,
          search,
          status,
          start_date,
          end_date,
        },
      }).then(res => {
        const { data } = res.data
        return {
          data,
        }
      }).catch(() => ({
        data: [],
      }))
    },
    /**
     * @description Mengambil data ringkasan tagihan dari server bisa diatur berdasarkan tanggal yang dipilih dari period.
     */
    fetchSummary() {
      this.isSummaryFetching = true
      this.$http_new.get('/auth/api/v1/admin/user-balance-invoices/summaries', {
        params: {
          start_date: YEAR_MONTH_DAY(this.period.startDate),
          end_date: YEAR_MONTH_DAY(this.period.endDate),
        },
      }).then(res => {
        this.summary = res.data.data
      }).catch(() => {
        this.summary = {
          nominal_invoice_this_month: 0,
          nominal_invoice_paid_this_month: 0,
          number_of_bill_this_month: 0,
          number_of_bill_paid_this_month: 0,
          nominal_invoice_last_month: 0,
          nominal_invoice_paid_last_month: 0,
          number_of_bill_paid_last_month: 0,
        }
        this.$toast_error({
          message: 'Gagal mengambil data tagihan, silahkan coba lagi.',
        })
      }).finally(() => {
        this.isSummaryFetching = false
      })
    },
  },
}
</script>
